import type { ContextMenuHandle, MenuItem } from "@ex0/menu";
import { createContextMenu } from "@ex0/menu";
import "./menubar.css";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function formatClock(date: Date): string {
  const day = DAY_NAMES[date.getDay()];
  const dayNum = date.getDate();
  const month = MONTH_NAMES[date.getMonth()];
  const year = date.getFullYear();
  const h = date.getHours();
  const m = date.getMinutes();
  const ampm = h < 12 ? "am" : "pm";
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const mins = m < 10 ? `0${m}` : String(m);
  return `${day} ${dayNum} ${month} ${year}  ${hour12}:${mins} ${ampm}`;
}

interface MenuButton {
  element: HTMLButtonElement;
  menu: ContextMenuHandle;
}

function createMenuButton(
  label: string,
  items: readonly MenuItem[],
  tracking: { active: boolean; justClosedButton: HTMLButtonElement | null },
  allButtons: MenuButton[],
): MenuButton {
  const button = document.createElement("button");
  button.className = "ex0-menubar__button";
  button.textContent = label;

  const menu = createContextMenu({
    items,
    onShow() {
      button.classList.add("ex0-menubar__button--active");
      tracking.active = true;
    },
    onHide() {
      button.classList.remove("ex0-menubar__button--active");
      tracking.active = false;
      tracking.justClosedButton = button;
      requestAnimationFrame(() => {
        tracking.justClosedButton = null;
      });
    },
  });

  button.addEventListener("click", () => {
    if (tracking.justClosedButton === button) {
      return;
    }
    if (menu.isVisible()) {
      menu.hide();
      return;
    }
    for (const other of allButtons) {
      if (other.menu.isVisible()) {
        other.menu.hide();
      }
    }
    const rect = button.getBoundingClientRect();
    menu.show(rect.left, rect.bottom);
  });

  button.addEventListener("pointerenter", () => {
    if (!tracking.active) {
      return;
    }
    const alreadyOpen = allButtons.some(
      (b) => b.element === button && b.menu.isVisible(),
    );
    if (alreadyOpen) {
      return;
    }
    for (const other of allButtons) {
      if (other.menu.isVisible()) {
        other.menu.hide();
      }
    }
    const rect = button.getBoundingClientRect();
    menu.show(rect.left, rect.bottom);
  });

  return { element: button, menu };
}

const noop = () => {};

export function createMenubar(root: HTMLElement): { dispose(): void } {
  const bar = document.createElement("div");
  bar.className = "ex0-menubar";

  const left = document.createElement("div");
  left.className = "ex0-menubar__left";

  const right = document.createElement("div");
  right.className = "ex0-menubar__right";

  const tracking = {
    active: false,
    justClosedButton: null as HTMLButtonElement | null,
  };
  const allButtons: MenuButton[] = [];

  const systemBtn = createMenuButton(
    "System",
    [
      { label: "About ex0...", action: noop },
      { type: "separator" },
      { label: "Shutdown", action: noop },
    ],
    tracking,
    allButtons,
  );
  systemBtn.element.classList.add("ex0-menubar__button--primary");
  allButtons.push(systemBtn);

  const optionsBtn = createMenuButton(
    "Options",
    [
      { label: "Preferences", action: noop },
      { label: "Display Settings", action: noop },
    ],
    tracking,
    allButtons,
  );
  allButtons.push(optionsBtn);

  const goodiesBtn = createMenuButton(
    "Goodies",
    [
      { label: "Calculator", disabled: true, action: noop },
      { label: "Notepad", disabled: true, action: noop },
    ],
    tracking,
    allButtons,
  );
  allButtons.push(goodiesBtn);

  left.appendChild(systemBtn.element);
  left.appendChild(optionsBtn.element);
  left.appendChild(goodiesBtn.element);

  const urlInput = document.createElement("input");
  urlInput.type = "text";
  urlInput.className = "ex0-menubar__url-input";
  urlInput.placeholder = "http://";

  const goButton = document.createElement("button");
  goButton.className = "ex0-menubar__go-button";
  goButton.textContent = "go.";

  const clock = document.createElement("span");
  clock.className = "ex0-menubar__clock";
  clock.textContent = formatClock(new Date());

  const intervalId = setInterval(() => {
    clock.textContent = formatClock(new Date());
  }, 1_000);

  function createSeparator(): HTMLSpanElement {
    const sep = document.createElement("span");
    sep.className = "ex0-menubar__separator";
    return sep;
  }

  right.appendChild(urlInput);
  right.appendChild(createSeparator());
  right.appendChild(goButton);
  right.appendChild(createSeparator());
  right.appendChild(clock);

  bar.appendChild(left);
  bar.appendChild(right);
  root.appendChild(bar);

  function dispose() {
    clearInterval(intervalId);
    for (const btn of allButtons) {
      btn.menu.destroy();
    }
    bar.remove();
  }

  return { dispose };
}
